<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService'; // <-- IMPORT THIS
  export let id; // From the router path

  let achievement = null;
  let promotion = null; 
  let transactions = [];
  let isLoading = true;
  let error = '';

  // Form state for adding a new transaction
  let newTransactionAmount = '';
  let isSubmitting = false;

  onMount(async () => {
    await fetchDetails();
  });

  async function fetchDetails() {
    isLoading = true;
    try {
      const response = await authFetch(getApiUrl(`/achievements/${id}`));
      if (!response.ok) throw new Error('Could not fetch achievement details.');
      const data = await response.json();
      achievement = data.achievement;
      promotion = data.promotion;
      transactions = data.transactions || [];
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  async function handleAddTransaction() {
    isSubmitting = true;
    error = '';
    try {
      const amount = parseFloat(newTransactionAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid, positive transaction amount.');
      }
      const payload = {
        achievementId: id,
        amount: amount,
      };
      const response = await authFetch(getApiUrl('/achievements/transactions'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to add transaction.');
      }
      newTransactionAmount = ''; // Clear input
      await fetchDetails(); // Refresh all data
    } catch (e) {
      error = e.message;
    } finally {
      isSubmitting = false;
    }
  }

  // Reactive properties
  $: maxTxTarget = achievement && achievement.maximumTargetNumTransactions > 0
    ? achievement.maximumTargetNumTransactions
    : (achievement ? achievement.targetNumTransactions : 0);

  $: maxSpendTarget = achievement && achievement.maximumTargetAccumulatedSpending > 0
    ? achievement.maximumTargetAccumulatedSpending
    : (achievement ? achievement.minimumTargetAccumulatedSpending : 0);

  $: progressPercentTransactions = maxTxTarget > 0
    ? (achievement.actualNumTransactions / maxTxTarget) * 100
    : 0;

  $: progressPercentSpending = maxSpendTarget > 0
    ? (achievement.actualAccumulatedSpending / maxSpendTarget) * 100
    : 0;

  $: interimTxPercent = maxTxTarget > 0 && achievement.maximumTargetNumTransactions > 0 && achievement.targetNumTransactions > 0
    ? (achievement.targetNumTransactions / maxTxTarget) * 100
    : null;

  $: interimSpendPercent = maxSpendTarget > 0 && achievement.maximumTargetAccumulatedSpending > 0 && achievement.minimumTargetAccumulatedSpending > 0
    ? (achievement.minimumTargetAccumulatedSpending / maxSpendTarget) * 100
    : null;
    
  $: isExpired = achievement ? new Date() > new Date(achievement.targetCompletionDate) : false;

  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  }
</script>

<div class="page-container">
  {#if isLoading}
    <p>Loading achievement details...</p>
  {:else if error}
    <p class="error">{error}</p>
  {:else if achievement && promotion}
    <div class="promo-info-card">
      <h2>{promotion.name}</h2>
      <p class="description">{promotion.description}</p>
      <div class="promo-details">
        {#if promotion.categories && promotion.categories.length > 0}
          <div><strong>Categories:</strong> {promotion.categories.join(', ')}</div>
        {/if}
        {#if promotion.minimumSpending != null && promotion.minimumSpending > 0}
          <div><strong>Min. Spend per Transaction:</strong> ${promotion.minimumSpending.toFixed(2)}</div>
        {/if}
        {#if promotion.targetNumTransactions != null && promotion.targetNumTransactions > 0}
          <div><strong>Target Transactions:</strong> {promotion.targetNumTransactions}</div>
        {/if}
        {#if promotion.maximumTargetNumTransactions != null && promotion.maximumTargetNumTransactions > 0}
          <div><strong>Max. Target Transactions:</strong> {promotion.maximumTargetNumTransactions}</div>
        {/if}
        {#if promotion.minimumTargetAccumulatedSpending != null && promotion.minimumTargetAccumulatedSpending > 0}
          <div><strong>Min. Target Spending:</strong> ${promotion.minimumTargetAccumulatedSpending.toFixed(2)}</div>
        {/if}
        {#if promotion.maximumTargetAccumulatedSpending != null && promotion.maximumTargetAccumulatedSpending > 0}
          <div><strong>Max. Target Spending:</strong> ${promotion.maximumTargetAccumulatedSpending.toFixed(2)}</div>
        {/if}
      </div>
    </div>
    
    <h3>My Progress</h3>
    
    <div class="total-reward">
      Total Reward Earned: <span>${(achievement.totalRewardAmount || 0).toFixed(2)}</span>
    </div>

    <div class="progress-container">
      {#if maxTxTarget > 0}
        <div class="progress-card">
          <label>Transactions</label>
          <div class="progress-bar" style="position: relative;">
            <div class="progress-fill" style="width: {Math.min(progressPercentTransactions, 100)}%;"></div>
            {#if interimTxPercent !== null}
              <div class="interim-tick" style="left: {interimTxPercent}%;" title="Interim Target: {achievement.targetNumTransactions}"></div>
            {/if}
          </div>
          <div class="progress-text">
            {#if achievement.maximumTargetNumTransactions > 0 && achievement.targetNumTransactions > 0}
              {achievement.actualNumTransactions} / {achievement.maximumTargetNumTransactions} <span class="interim-label">(Interim Target: {achievement.targetNumTransactions})</span>
            {:else if achievement.maximumTargetNumTransactions > 0}
              {achievement.actualNumTransactions} / {achievement.maximumTargetNumTransactions}
            {:else}
              {achievement.actualNumTransactions} / {achievement.targetNumTransactions}
            {/if}
          </div>
        </div>
      {/if}
      
      {#if maxSpendTarget > 0}
        <div class="progress-card">
          <label>Accumulated Spending</label>
          <div class="progress-bar" style="position: relative;">
            <div class="progress-fill" style="width: {Math.min(progressPercentSpending, 100)}%;"></div>
            {#if interimSpendPercent !== null}
              <div class="interim-tick" style="left: {interimSpendPercent}%;" title="Interim Target: ${achievement.minimumTargetAccumulatedSpending.toFixed(2)}"></div>
            {/if}
          </div>
          <div class="progress-text">
            {#if achievement.maximumTargetAccumulatedSpending > 0 && achievement.minimumTargetAccumulatedSpending > 0}
              ${achievement.actualAccumulatedSpending.toFixed(2)} / ${achievement.maximumTargetAccumulatedSpending.toFixed(2)} <span class="interim-label">(Interim Target: ${achievement.minimumTargetAccumulatedSpending.toFixed(2)})</span>
            {:else if achievement.maximumTargetAccumulatedSpending > 0}
              ${achievement.actualAccumulatedSpending.toFixed(2)} / ${achievement.maximumTargetAccumulatedSpending.toFixed(2)}
            {:else}
              ${achievement.actualAccumulatedSpending.toFixed(2)} / ${achievement.minimumTargetAccumulatedSpending.toFixed(2)}
            {/if}
          </div>
        </div>
      {/if}
    </div>

    <!-- Add Transaction Form -->
    <div class="form-section">
      <h4>Log a New Transaction</h4>
      {#if isExpired}
        <p class="expired-note">This promotion has ended. You can no longer log new transactions.</p>
      {:else}
        <form on:submit|preventDefault={handleAddTransaction}>
          <input type="number" step="0.01" min="0.01" bind:value={newTransactionAmount} placeholder="Enter transaction amount ($)" required />
          <button type="submit" disabled={isSubmitting}>
            {#if isSubmitting}Logging...{:else}Log Transaction{/if}
          </button>
        </form>
      {/if}
    </div>

    <!-- Transaction History -->
    <div class="history-section">
      <h4>Transaction History</h4>
      {#if transactions.length === 0}
        <p>No transactions have been logged for this achievement yet.</p>
      {:else}
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Amount</th>
                <th>Is Eligible</th>
                <th>Reward Earned</th>
              </tr>
            </thead>
            <tbody>
              {#each transactions as trans (trans._id)}
                <tr>
                  <!-- Add data-label for mobile CSS -->
                  <td data-label="Date & Time">{formatDate(trans.transactionDateTime)}</td>
                  <td data-label="Amount">${trans.amount.toFixed(2)}</td>
                  <td data-label="Is Eligible">{trans.isEligible ? '✔️' : '❌'}</td>
                  <td data-label="Reward Earned" class="reward-cell">${(trans.rewardAmount || 0).toFixed(2)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
        <p class="eligibility-note">
          Note: A transaction is marked as "Eligible" (✔️) if its amount meets the promotion's "Minimum Spending Per Transaction" requirement. Only eligible transactions count towards your progress and rewards.
        </p>
      {/if}
    </div>
  {/if}
</div>

<style>
  .page-container { max-width: 800px; margin: 2rem auto; padding: 0 1rem; }
  .promo-info-card {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    padding: 1.5rem;
    border-radius: 8px;
    margin-bottom: 2rem;
    text-align: left;
  }
  .promo-info-card h2 { margin-top: 0; }
  .promo-info-card .description { color: #555; }
  .promo-info-card .promo-details { font-size: 0.9rem; margin-top: 1rem; }
  h3 { text-align: center; }
  
  .total-reward {
    font-size: 1.2rem;
    font-weight: bold;
    text-align: center;
    margin: 2rem 0;
    padding: 1rem;
    background-color: #e2f0d9;
    color: #28a745;
    border-radius: 8px;
  }
  .total-reward span {
    font-size: 1.5rem;
  }

  .progress-container { display: grid; grid-template-columns: 1fr; gap: 1.5rem; margin: 2rem 0; }
  @media (min-width: 768px) { .progress-container { grid-template-columns: 1fr 1fr; } }
  .progress-card { background: #fff; padding: 1.5rem; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
  .progress-bar { width: 100%; background-color: #e9ecef; border-radius: 1rem; height: 1.5rem; overflow: hidden; margin: 0.5rem 0; }
  .progress-fill { height: 100%; background-color: #28a745; border-radius: 1rem; transition: width 0.5s ease-in-out; }
  .progress-text { text-align: right; font-weight: bold; }
  
  /* --- Responsive Form Styles --- */
  .form-section { margin-top: 3rem; }
  
  /* Default: Stacked layout (Mobile First) */
  form { 
      display: flex; 
      flex-direction: column; 
      gap: 1rem; 
  }
  
  input { 
      width: 100%; /* Full width on mobile */
      padding: 0.75rem; 
      border: 1px solid #ccc; 
      border-radius: 4px; 
      font-size: 1rem; 
      box-sizing: border-box;
  }
  
  button { 
      width: 100%; /* Full width on mobile */
      padding: 0.75rem 1.5rem; 
      background-color: #007bff; 
      color: white; 
      border: none; 
      border-radius: 4px; 
      cursor: pointer; 
      font-size: 1.1rem;
  }

  /* Tablet/Desktop: Horizontal layout */
  @media (min-width: 600px) {
      form {
          flex-direction: row;
      }
      button {
          width: auto;
      }
  }
  
  button:disabled { background-color: #aaa; }
  .expired-note { color: #dc3545; font-weight: bold; }

  /* --- Responsive Table Styles --- */
  .history-section { margin-top: 3rem; }
  .table-wrapper { width: 100%; }
  
  table { 
      width: 100%; 
      border-collapse: collapse; 
      background: #fff; 
      border-radius: 8px; 
      overflow: hidden; 
  }
  
  th, td { 
      padding: 12px 15px; 
      text-align: left; 
      border-bottom: 1px solid #eee; 
  }
  
  thead { background-color: #f8f9fa; }
  
  .reward-cell {
      font-weight: bold;
      color: #28a745;
  }

  /* Mobile Table Transformation */
  @media (max-width: 768px) {
      thead {
          display: none; /* Hide headers */
      }
      
      table, tbody, tr, td {
          display: block;
          width: 100%;
          box-sizing: border-box;
      }
      
      tr {
          margin-bottom: 1rem;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          background: #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
      }
      
      td {
          text-align: right;
          padding: 10px 15px;
          border-bottom: 1px solid #f1f1f1;
          display: flex;
          justify-content: space-between;
          align-items: center;
      }
      
      td:last-child {
          border-bottom: none;
      }
      
      /* Add label using the data attribute */
      td::before {
          content: attr(data-label);
          float: left;
          font-weight: bold;
          color: #6c757d;
          font-size: 0.9rem;
      }
  }

  .eligibility-note {
    font-size: 0.85rem;
    color: #6c757d;
    margin-top: 1rem;
    background-color: #f8f9fa;
    padding: 0.75rem;
    border-radius: 4px;
  }
  .error { color: red; text-align: center; }
  .interim-tick {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: #dc3545;
    z-index: 2;
    transform: translateX(-50%);
  }
  .interim-label {
    font-size: 0.8rem;
    font-weight: normal;
    color: #6c757d;
  }
</style>